<template>
  <div class="student-discipline-tab">
    <!-- Header Summary -->
    <div class="row mb-4">
      <div class="col-md-2">
        <a-card class="summary-card">
          <div class="text-center">
            <div class="summary-value text-primary">{{ summary.total }}</div>
            <div class="summary-label">Total Incidents</div>
          </div>
        </a-card>
      </div>
       <div class="col-md-2">
        <a-card class="summary-card">
          <div class="text-center">
            <div class="summary-value text-success">{{ summary.totalPenalties }}</div>
            <div class="summary-label">Total Penalties</div>
          </div>
        </a-card>
      </div>
      <div class="col-md-2">
        <a-card class="summary-card">
          <div class="text-center">
            <div class="summary-value text-danger">{{ summary.major }}</div>
            <div class="summary-label">Major</div>
          </div>
        </a-card>
      </div>
      <div class="col-md-2">
        <a-card class="summary-card">
          <div class="text-center">
            <div class="summary-value text-warning">{{ summary.medium }}</div>
            <div class="summary-label">Medium</div>
          </div>
        </a-card>
      </div>
      <div class="col-md-2">
        <a-card class="summary-card">
          <div class="text-center">
            <div class="summary-value text-info">{{ summary.minor }}</div>
            <div class="summary-label">Minor</div>
          </div>
        </a-card>
      </div>
    </div>
    <!-- Records Table -->
    <a-card>
      <a-table
        :columns="columns"
        :dataSource="data"
        :pagination="pagination"
        :loading="loading"
        :rowKey="record => record.id"
        @change="handleTableChange"
      >
        <template slot="incident_date" slot-scope="date">
          {{ date | formatDate }}
        </template>

        <template slot="severity" slot-scope="severity">
          <a-tag :color="getSeverityColor(severity)" class="severity-tag">
            {{ severity | capitalize }}
          </a-tag>
        </template>

        <template slot="description" slot-scope="description">
          <a-tooltip v-if="description && description.length > 50" :title="description">
            {{ description.substring(0, 50) }}...
          </a-tooltip>
          <span v-else>{{ description || '-' }}</span>
        </template>

        <template slot="actions" slot-scope="record">
          <div class="text-nowrap">
            <!-- View -->
            <a-tooltip placement="top" title="View Record Details">
              <a-button
                type="primary"
                size="small"
                @click="handleView(record)"
                class="action-btn"
              >
                <a-icon type="eye" />
              </a-button>
            </a-tooltip>
          </div>
        </template>
      </a-table>
    </a-card>

    <!-- View Modal -->
    <view-discipline-modal
      :visible="isViewModalVisible"
      :recordId="viewRecordId"
      @close="isViewModalVisible = false"
    />
  </div>
</template>

<script>
import ViewDisciplineModal from '@/components/disciplineManagement/display/disciplineView'
import moment from 'moment'

export default {
  name: 'StudentDisciplineTab',
  components: {
    ViewDisciplineModal,
  },
  props: {
    studentId: {
      type: [String, Number],
      required: true,
    },
    studentInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      data: [],
      summary: {
        total: 0,
        major: 0,
        medium: 0,
        minor: 0,
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      },
      viewRecordId: null,
      isViewModalVisible: false,
      submitting: false,
      exportLoading: false,
      columns: [
        {
          title: 'Date',
          dataIndex: 'incident_date',
          key: 'incident_date',
          sorter: true,
          width: '120px',
          scopedSlots: { customRender: 'incident_date' },
        },
        {
          title: 'Category',
          dataIndex: 'category_name',
          key: 'category_name',
          sorter: true,
          width: '150px',
        },
        {
          title: 'Description',
          dataIndex: 'description',
          key: 'description',
          width: '150px',
          scopedSlots: { customRender: 'description' },
        },
        {
          title: 'Penalty',
          dataIndex: 'penalty',
          key: 'penalty',
          width: '150px',
        },
        {
          title: 'Severity',
          dataIndex: 'severity',
          key: 'severity',
          width: '100px',
          scopedSlots: { customRender: 'severity' },
          sorter: true,
        },
        {
          title: 'Reported By',
          dataIndex: 'reported_by',
          key: 'reported_by',
          width: '150px',
        },
        {
          title: 'Actions',
          key: 'actions',
          width: '120px',
          scopedSlots: { customRender: 'actions' },
        },
      ],
    }
  },
  computed: {
    modalTitle() {
      return this.editingRecord ? 'Edit Discipline Record' : 'Add Discipline Record'
    },
  },
  mounted() {
    this.fetchDisciplineRecords()
  },
  watch: {
    studentId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchDisciplineRecords()
        }
      },
    },
  },
  methods: {
    fetchDisciplineRecords() {
      this.loading = true
      this.$http.get('/api/student-discipline', {
        params: {
          student_id: this.studentId,
          limit: this.pagination.pageSize,
          page: this.pagination.current,
        },
      })
        .then(({ data }) => {
          this.data = data.data.map(item => ({
            id: item.id,
            incident_date: item.incident_date,
            category_name: item.category ? item.category.name : '-',
            description: item.description || '-',
            penalty: item.penalty ? item.penalty.name : '-',
            penalty_details: item.penalty_details || '-',
            severity: item.severity || '-',
            reported_by: item.reported_by
              ? `${item.reported_by.first_name} ${item.reported_by.last_name}`
              : '-',
            reported_by_id: item.reported_by ? item.reported_by.id : null,
            category_id: item.category_id,
            penalty_id: item.penalty_id,
          }))

          this.pagination.total = data.meta.total
          this.calculateSummary(this.data)
          this.loading = false
        })
        .catch(error => {
          console.error('Failed to fetch discipline records:', error)
          this.$neoNotify.error('Failed to load discipline records')
          this.loading = false
        })
    },

    calculateSummary(records) {
      this.summary.total = records.length
      this.summary.major = records.filter(r => r.severity === 'major').length
      this.summary.medium = records.filter(r => r.severity === 'medium').length
      this.summary.minor = records.filter(r => r.severity === 'minor').length
      const recordsWithPenalties = records.filter(r => r.penalty_id !== null)
      this.summary.totalPenalties = recordsWithPenalties.length
    },

    getSeverityColor(severity) {
      const colors = {
        minor: 'blue',
        medium: 'orange',
        major: 'red',
      }
      return colors[severity] || 'default'
    },
    handleView(record) {
      this.viewRecordId = record.id
      this.isViewModalVisible = true
    },

    handleRecordCreated() {
      this.isModalVisible = false
      this.fetchDisciplineRecords()
      this.$notification.success({
        message: 'Success',
        description: 'Discipline record added successfully',
      })
    },

    handleTableChange(pagination, filters, sorter) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.fetchDisciplineRecords()
    },
  },
  filters: {
    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : '-'
    },
    capitalize(value) {
      if (!value) return ''
      return value.charAt(0).toUpperCase() + value.slice(1)
    },
  },
}
</script>

<style scoped>
.student-discipline-tab {
  padding: 20px 0;
}

.summary-card {
  border-radius: 8px;
  text-align: center;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
}

.summary-label {
  font-size: 14px;
  color: #666;
}

.severity-tag {
  margin: 0;
  font-weight: 500;
}

.action-btn {
  padding: 4px 8px;
  height: auto;
}
</style>
